/**
 * Cognitoエラーメッセージの日本語翻訳マップ
 * 
 * AWS Cognitoから返されるエラーメッセージを日本語に翻訳するためのユーティリティ
 */

export interface CognitoErrorTranslation {
  ja: string;
  en: string;
}

/**
 * Cognitoエラーコードと翻訳のマッピング
 */
export const COGNITO_ERROR_TRANSLATIONS: Record<string, CognitoErrorTranslation> = {
  // 認証エラー
  NotAuthorizedException: {
    ja: 'メールアドレスまたはパスワードが正しくありません。',
    en: 'Incorrect username or password.'
  },
  UserNotFoundException: {
    ja: 'ユーザーが見つかりません。',
    en: 'User does not exist.'
  },
  UserNotConfirmedException: {
    ja: 'ユーザーの確認が完了していません。メールをご確認ください。',
    en: 'User is not confirmed.'
  },
  PasswordResetRequiredException: {
    ja: 'パスワードのリセットが必要です。',
    en: 'Password reset required.'
  },
  
  // パスワード関連エラー
  InvalidPasswordException: {
    ja: 'パスワードが要件を満たしていません。',
    en: 'Invalid password.'
  },
  LimitExceededException: {
    ja: '試行回数が上限を超えました。しばらくしてから再度お試しください。',
    en: 'Attempt limit exceeded, please try again later.'
  },
  TooManyRequestsException: {
    ja: 'リクエストが多すぎます。しばらくしてから再度お試しください。',
    en: 'Too many requests, please try again later.'
  },
  TooManyFailedAttemptsException: {
    ja: 'ログイン試行回数が上限を超えました。しばらくしてから再度お試しください。',
    en: 'Password attempts exceeded.'
  },
  
  // 確認コード関連エラー
  CodeMismatchException: {
    ja: '確認コードが正しくありません。もう一度お試しください。',
    en: 'Invalid verification code provided, please try again.'
  },
  ExpiredCodeException: {
    ja: '確認コードの有効期限が切れています。新しいコードを送信してください。',
    en: 'Verification code has expired, please request a new code.'
  },
  CodeDeliveryFailureException: {
    ja: '確認コードの送信に失敗しました。',
    en: 'Failed to deliver verification code.'
  },
  
  // ユーザー登録関連エラー
  UsernameExistsException: {
    ja: 'このメールアドレスは既に登録されています。',
    en: 'An account with the given email already exists.'
  },
  InvalidParameterException: {
    ja: '入力内容が正しくありません。もう一度ご確認ください。',
    en: 'Invalid parameter.'
  },
  
  // セッション・トークン関連エラー
  NotAuthenticatedException: {
    ja: '認証が必要です。ログインしてください。',
    en: 'Not authenticated.'
  },
  InvalidTokenException: {
    ja: 'セッションの有効期限が切れました。再度ログインしてください。',
    en: 'Invalid token.'
  },
  TokenRefreshException: {
    ja: 'セッションの更新に失敗しました。再度ログインしてください。',
    en: 'Failed to refresh token.'
  },
  
  // ネットワーク・サーバーエラー
  NetworkError: {
    ja: 'ネットワークエラーが発生しました。接続を確認してください。',
    en: 'Network error occurred.'
  },
  ServiceUnavailable: {
    ja: 'サービスが一時的に利用できません。しばらくしてから再度お試しください。',
    en: 'Service temporarily unavailable.'
  },
  InternalErrorException: {
    ja: 'システムエラーが発生しました。しばらくしてから再度お試しください。',
    en: 'Internal server error.'
  },
  
  // その他のエラー
  ResourceNotFoundException: {
    ja: 'リソースが見つかりません。',
    en: 'Resource not found.'
  },
  AliasExistsException: {
    ja: 'このメールアドレスは既に使用されています。',
    en: 'An account with this email already exists.'
  },
  MFAMethodNotFoundException: {
    ja: '多要素認証の設定が見つかりません。',
    en: 'MFA method not found.'
  },
  SoftwareTokenMFANotFoundException: {
    ja: '多要素認証トークンが見つかりません。',
    en: 'Software token MFA not found.'
  },
  InvalidLambdaResponseException: {
    ja: 'システムエラーが発生しました。管理者にお問い合わせください。',
    en: 'Invalid lambda response.'
  },
  UnexpectedLambdaException: {
    ja: 'システムエラーが発生しました。管理者にお問い合わせください。',
    en: 'Unexpected lambda exception.'
  },
  UserLambdaValidationException: {
    ja: 'ユーザー検証に失敗しました。',
    en: 'User lambda validation failed.'
  },
  
  // タイムアウト
  TIMEOUT: {
    ja: 'タイムアウトしました。ネットワーク接続を確認して再度お試しください。',
    en: 'Request timed out.'
  },
};

/**
 * エラーメッセージの翻訳マップ（メッセージベース）
 * Cognitoから返される具体的なメッセージを翻訳
 */
export const COGNITO_MESSAGE_TRANSLATIONS: Record<string, CognitoErrorTranslation> = {
  'Incorrect username or password.': {
    ja: 'メールアドレスまたはパスワードが正しくありません。',
    en: 'Incorrect username or password.'
  },
  'User does not exist.': {
    ja: 'ユーザーが見つかりません。',
    en: 'User does not exist.'
  },
  'Password attempts exceeded': {
    ja: 'パスワードの試行回数が上限を超えました。しばらくしてから再度お試しください。',
    en: 'Password attempts exceeded'
  },
  'Invalid verification code provided, please try again.': {
    ja: '確認コードが正しくありません。もう一度お試しください。',
    en: 'Invalid verification code provided, please try again.'
  },
  'User is not confirmed.': {
    ja: 'ユーザーの確認が完了していません。メールをご確認ください。',
    en: 'User is not confirmed.'
  },
  'User is disabled.': {
    ja: 'ユーザーアカウントが無効化されています。',
    en: 'User is disabled.'
  },
  'User already exists': {
    ja: 'ユーザーは既に存在します。',
    en: 'User already exists'
  },
  'Username and password are required': {
    ja: 'ユーザー名とパスワードは必須です。',
    en: 'Username and password are required'
  },
  'Failed to get tokens': {
    ja: '認証トークンの取得に失敗しました。',
    en: 'Failed to get tokens'
  },
  'Operation timed out': {
    ja: 'タイムアウトしました。もう一度お試しください。',
    en: 'Operation timed out'
  },
};

/**
 * Cognitoエラーを日本語に翻訳する
 * @param error エラーオブジェクト
 * @param language 言語（'ja' | 'en'）
 * @returns 翻訳されたエラーメッセージ
 */
export function translateCognitoError(
  error: any,
  language: 'ja' | 'en' = 'ja'
): string {
  // エラーが存在しない場合
  if (!error) {
    return language === 'ja' 
      ? '不明なエラーが発生しました。'
      : 'Unknown error occurred.';
  }

  // エラーコード（error.name）による翻訳
  if (error.name && COGNITO_ERROR_TRANSLATIONS[error.name]) {
    return COGNITO_ERROR_TRANSLATIONS[error.name][language];
  }

  // エラーコード（error.code）による翻訳
  if (error.code && COGNITO_ERROR_TRANSLATIONS[error.code]) {
    return COGNITO_ERROR_TRANSLATIONS[error.code][language];
  }

  // エラーメッセージによる翻訳
  const errorMessage = error.message || error.toString();
  if (errorMessage && COGNITO_MESSAGE_TRANSLATIONS[errorMessage]) {
    return COGNITO_MESSAGE_TRANSLATIONS[errorMessage][language];
  }

  // 部分一致による翻訳
  for (const [key, translation] of Object.entries(COGNITO_MESSAGE_TRANSLATIONS)) {
    if (errorMessage.includes(key)) {
      return translation[language];
    }
  }

  // 翻訳が見つからない場合は元のメッセージを返す
  return errorMessage || (language === 'ja' 
    ? '予期しないエラーが発生しました。'
    : 'An unexpected error occurred.');
}

/**
 * エラーオブジェクトに翻訳済みメッセージを追加する
 * @param error エラーオブジェクト
 * @param language 言語（'ja' | 'en'）
 * @returns 翻訳済みメッセージが追加されたエラーオブジェクト
 */
export function enrichErrorWithTranslation(
  error: any,
  language: 'ja' | 'en' = 'ja'
): any {
  if (!error) return error;

  return {
    ...error,
    translatedMessage: translateCognitoError(error, language),
    originalMessage: error.message,
  };
}

